import { type ActionCableJobStatusData } from "@/types";
import { flash, FlashKindEnum } from "@/utils/flash";

// Only a backstop for when the terminal broadcast never arrives at all (job
// never runs, worker killed mid-run, dropped cable). A hung external call is
// NOT this case: the backend's own HTTP timeouts raise and broadcast a
// `failed` with a specific message. The default sits deliberately ABOVE the
// backend worst case (chat up to 120s + model call + safety) so that specific
// backend error wins the race; the timeout only fires when nothing is coming.
// A result arriving after expiry is still accepted (see the onUpdate handler).
export const DEFAULT_JOB_BROADCAST_TIMEOUT_MS = 180_000;

/**
 * Which broadcasts on the channel belong to this consumer.
 *
 * A plain string selects by `action`, which is how most jobs announce
 * themselves. `{ name: … }` selects by the job class instead — the shape
 * `Tools::BaseActionWithStatusJob` broadcasts, which carries no `action` at all
 * (the contract documents the two as separate messages: see
 * `CurrentUserJobStatusMessage` and `CurrentUserActionJobStatusMessage`).
 *
 * `name` takes a list because several views wait on a whole family of bulk
 * actions at once — discard, destroy, undiscard and duplicate all end the same
 * waiting state.
 */
export type JobBroadcastSelector = string | { name: string | string[] };

export function matchesSelector(
  selector: JobBroadcastSelector,
  data: ActionCableJobStatusData
): boolean {
  if (typeof selector === "string") return data.action === selector;
  if (!data.name) return false;

  return Array.isArray(selector.name)
    ? selector.name.includes(data.name)
    : selector.name === data.name;
}

export type JobBroadcastFailure =
  | { reason: "failed"; data: ActionCableJobStatusData; payload: unknown }
  | { reason: "unexpected"; data: ActionCableJobStatusData; payload: unknown }
  | { reason: "timeout" };

interface UseJobBroadcastOptions {
  /**
   * Convenience correlation: only broadcasts whose `record_id` equals this
   * value (string/number-insensitive) are handled. While the value is
   * undefined, nothing matches.
   */
  recordId?: MaybeRefOrGetter<string | number | undefined>;
  /**
   * Custom correlation for consumers that key on something other than
   * `record_id` (payload id, request_id, url, …). Takes precedence over
   * `recordId`.
   */
  match?: (data: ActionCableJobStatusData, payload: unknown) => boolean;
  /**
   * What counts as a successful outcome. Defaults to
   * `data.status === "completed"`. Consumers with stricter contracts (e.g. a
   * completed broadcast must carry a file) narrow it here; everything else
   * terminal is routed to `onError` as `reason: "unexpected"`.
   */
  isSuccess?: (data: ActionCableJobStatusData, payload: unknown) => boolean;
  onSuccess: (data: ActionCableJobStatusData, payload: unknown) => void;
  onError?: (failure: JobBroadcastFailure) => void;
  /**
   * Called on the first connect after a socket drop or a re-created
   * subscription, while a wait is still running. Broadcasts sent into that gap
   * are gone — ActionCable keeps no history — so a job that finished in it
   * would never announce itself. The consumer refetches here and settles the
   * wait itself via `stop()` if the result is in; the timeout stays armed, so
   * a refetch that brings nothing is still caught.
   */
  onReconnect?: () => void;
  /**
   * Called for every `in_progress` broadcast, for consumers that render a
   * progress bar or a streamed partial result. The timeout stays armed: a job
   * that reports progress and then dies is still caught.
   */
  onProgress?: (data: ActionCableJobStatusData, payload: unknown) => void;
  /**
   * Restart the timeout on every `in_progress` broadcast, so it measures
   * silence rather than total runtime. For jobs that legitimately run far past
   * the default and keep reporting while they do — transcoding a long
   * recording. A job that dies still goes quiet and is still caught. Progress
   * after the timeout fired re-arms the wait, so a consumer showing that
   * progress again is bounded again.
   */
  restartTimeoutOnProgress?: boolean;
  timeoutMs?: number;
  /** Flashed when the timeout backstop fires. Defaults to `errors.jobTimeout`. */
  timeoutMessage?: MaybeRefOrGetter<string>;
  /**
   * Whether the timeout backstop flashes. A consumer that can tell from its own
   * data whether the job finished (and would otherwise show "aborted" over a
   * result that is actually there) turns this off and reports in `onError`.
   */
  flashOnTimeout?: boolean;
  /**
   * Flashed on an unexpected terminal broadcast (`reason: "unexpected"`).
   * Defaults to `errors.jobProcessingFailed`. Explicit `failed` broadcasts are
   * never flashed here — useJobStatusChannel already toasts those with the
   * backend-localized message, so flashing again would double-notify.
   */
  failureMessage?: MaybeRefOrGetter<string>;
}

/**
 * Tracks a single background job over the CurrentUserJobStatusChannel until a
 * terminal broadcast arrives, with a timeout backstop so consumers never hang
 * in a loading state forever.
 *
 * Call `start()` when the job is enqueued: it sets `isRunning` and arms the
 * timeout. `in_progress` broadcasts only reach `onProgress` — the timeout
 * stays armed so a job that dies after starting is still caught. Any terminal
 * broadcast clears the timeout and `isRunning`, then routes to `onSuccess` or
 * `onError`. Call `stop()` yourself when the job never got enqueued (e.g. the
 * enqueue mutation rejected) — no broadcast will ever arrive on that path, and
 * equally when `onReconnect` told you the result is already in.
 */
export function useJobBroadcast(
  selector: JobBroadcastSelector,
  {
    recordId,
    match,
    isSuccess = (data) => data.status === "completed",
    onSuccess,
    onError,
    onReconnect,
    onProgress,
    restartTimeoutOnProgress = false,
    timeoutMs = DEFAULT_JOB_BROADCAST_TIMEOUT_MS,
    timeoutMessage,
    flashOnTimeout = true,
    failureMessage,
  }: UseJobBroadcastOptions
): {
  isRunning: Ref<boolean>;
  start: () => void;
  stop: () => void;
} {
  const { t } = useI18n();
  const isRunning = ref(false);
  // Set when the timeout ended the wait, cleared by anything else that ends or
  // restarts it. A job that reports progress after that is alive after all.
  let hasTimedOut = false;

  const { start: startTimeout, stop: stopTimeout } = useTimeoutFn(
    () => {
      isRunning.value = false;
      hasTimedOut = true;
      if (flashOnTimeout) {
        flash(
          FlashKindEnum.ALERT,
          toValue(timeoutMessage) ?? t("errors.jobTimeout")
        );
      }
      onError?.({ reason: "timeout" });
    },
    timeoutMs,
    { immediate: false }
  );

  const start = (): void => {
    hasTimedOut = false;
    isRunning.value = true;
    startTimeout();
  };

  const stop = (): void => {
    hasTimedOut = false;
    stopTimeout();
    isRunning.value = false;
  };

  const matchesBroadcast = (
    data: ActionCableJobStatusData,
    payload: unknown
  ): boolean => {
    if (match) return match(data, payload);
    if (recordId !== undefined) {
      const expected = toValue(recordId);
      return (
        expected != null &&
        data.record_id != null &&
        String(data.record_id) === String(expected)
      );
    }
    return true;
  };

  const isMine = (data: ActionCableJobStatusData): boolean =>
    matchesSelector(selector, data);

  useJobStatusChannel({
    // Registered only when the consumer asked for it, so a consumer without one
    // does not sit in the channel's listener list doing nothing.
    onReconnect:
      onReconnect &&
      (() => {
        if (isRunning.value) onReconnect();
      }),
    onUpdate: (data, payload) => {
      if (!isMine(data)) return;
      if (!matchesBroadcast(data, payload)) return;

      // "in_progress" is not terminal — leave the timeout armed so a job that
      // dies after starting is still caught. Only terminal statuses below.
      if (data.status === "in_progress") {
        // Progress after the timeout fired re-arms the wait: the consumer is
        // about to show that progress again, and it must not be able to stick.
        if (restartTimeoutOnProgress && (isRunning.value || hasTimedOut)) {
          start();
        }
        onProgress?.(data, payload);
        return;
      }

      // Deliberately not gated on isRunning: a result arriving after the
      // timeout already expired is still delivered.
      stop();

      if (isSuccess(data, payload)) {
        onSuccess(data, payload);
      } else if (data.status === "failed") {
        // The channel already toasts failed broadcasts — no flash here.
        onError?.({ reason: "failed", data, payload });
      } else {
        flash(
          FlashKindEnum.ALERT,
          toValue(failureMessage) ?? t("errors.jobProcessingFailed")
        );
        onError?.({ reason: "unexpected", data, payload });
      }
    },
  });

  return { isRunning, start, stop };
}
